import { Controller } from '@hotwired/stimulus';
import { isMobileViewport } from '../../../../utils/customer/is_mobile_viewport';

/**
 * チケット確認画面全体を管理するコントローラ
 */
export default class extends Controller {
  static override targets = [
    'contentsEventModal',
    'eventConfirmationContents',
    'modalToggleIcon',
    'submitButton',
  ];

  declare readonly contentsEventModalTarget: HTMLElement;
  declare readonly eventConfirmationContentsTarget: HTMLElement;
  declare readonly submitButtonTarget: HTMLElement;
  declare readonly modalToggleIconTarget: HTMLElement;
  declare readonly hasModalToggleIconTarget: boolean;

  private isModalOpen = true;
  private hasScrolled = false;

  override connect() {
    this.updateToggleIcon();
    this.adjustPaddingTopOnPage();
    window.addEventListener('scroll', this.onTicketGroupScroll);
  }

  override disconnect() {
    window.removeEventListener('scroll', this.onTicketGroupScroll);
  }

  /**
   * チケットグループ画面のスクロールイベント
   */
  private onTicketGroupScroll = () => {
    if (this.hasScrolled) return;

    this.toggleEventModal();
    this.hasScrolled = true;

    window.removeEventListener('scroll', this.onTicketGroupScroll);
  };

  /**
   * イベントモーダルで隠れた部分をpaddingで調整する
   */
  private adjustPaddingTopOnPage = () => {
    const element = this.contentsEventModalTarget;
    const target = this.eventConfirmationContentsTarget;
    if (!element || !target) return;

    const computedHeight = parseFloat(getComputedStyle(element).height);
    target.style.paddingTop = `${computedHeight + 40}px`;
  };

  /**
   * モーダルを開く/閉じる
   */
  toggleEventModal() {
    const element = this.contentsEventModalTarget;
    if (!element) return;

    if (this.isModalOpen) {
      this.collapse(element);
    } else {
      this.expand(element);
    }
  }

  /**
   * トグルボタンのアイコンを開閉状態に応じて更新
   */
  private updateToggleIcon() {
    if (!this.hasModalToggleIconTarget) return;
    this.modalToggleIconTarget.textContent = this.isModalOpen
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  /**
   * 同意チェックの変更ハンドラ
   */
  agreement(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    const isChecked = input.checked;
    // button 要素であれば disabled プロパティを更新
    if ('disabled' in this.submitButtonTarget) {
      (this.submitButtonTarget as HTMLButtonElement).disabled = !isChecked;
    }

    // a/button 双方で効くよう aria-disabled を切り替え
    this.submitButtonTarget.setAttribute('aria-disabled', !isChecked ? 'true' : 'false');
  }

  /**
   * モーダルを開く
   */
  private expand(element: HTMLElement) {
    this.isModalOpen = true;

    const styles = getComputedStyle(element);
    const currentHeightPx = parseFloat(styles.height || '0');

    // コンテンツの実高さ
    const contentHeightPx = element.scrollHeight;

    // max-height を px で取得（無い場合は 90vh を px 換算）
    let maxHeightPx = parseFloat(styles.maxHeight);
    if (Number.isNaN(maxHeightPx) || !Number.isFinite(maxHeightPx)) {
      maxHeightPx = Math.round(window.innerHeight * 0.9);
    }

    // 90vh を超えないようにアニメーションの目標値を制限
    const targetHeightPx = Math.min(contentHeightPx, maxHeightPx);

    // 現在値から目標値へトランジション
    element.style.height = `${currentHeightPx}px`;

    // reflow を強制してトランジションを確実に開始
    void element.offsetHeight;
    element.style.height = `${targetHeightPx}px`;

    // スクロールイベントを有効化
    window.addEventListener('scroll', this.onTicketGroupScroll);
    this.hasScrolled = false;

    this.updateToggleIcon();

    const onEnd = (ev: TransitionEvent) => {
      if (ev.propertyName !== 'height') return;

      // トランジション終了後は auto に戻す（max-height により 90vh を超えない）
      element.style.height = 'auto';
      element.removeEventListener('transitionend', onEnd);

      this.adjustPaddingTopOnPage();
    };
    element.addEventListener('transitionend', onEnd);
  }

  /**
   * モーダルを閉じる
   */
  private collapse(element: HTMLElement) {
    this.isModalOpen = false;

    // auto の場合でも一旦ピクセル固定にしてから 0 にする
    const computedHeight = parseFloat(getComputedStyle(element).height || '0');
    element.style.height = `${computedHeight}px`;

    void element.offsetHeight;
    element.style.height = `${isMobileViewport() ? 90 : 102}px`;

    this.hasScrolled = true;

    this.updateToggleIcon();

    const onEnd = (ev: TransitionEvent) => {
      if (ev.propertyName !== 'height') return;
      // 閉じた状態を維持
      element.removeEventListener('transitionend', onEnd);

      this.adjustPaddingTopOnPage();
    };
    element.addEventListener('transitionend', onEnd);
  }
}
